import React from 'react';
import { User, Zap, Target, TrendingUp, Award, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <User className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            About the Developer
          </h1>
          <p className="text-2xl text-orange-500 font-semibold">
            Built by a Builder, Not a Corporation
          </p>
        </div>

        {/* Main Story */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 md:p-12 mb-12">
          <div className="prose prose-invert max-w-none">
            <p className="text-xl text-gray-300 leading-relaxed mb-6">
              Hi, I'm <span className="text-orange-500 font-semibold">Tusdang (Đặng Việt Tú)</span> — the founder and developer behind Twe Voice Assistant.
            </p>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              <strong className="text-white">Twe isn't a corporate experiment.</strong>
              <br />
              It's the result of years of self-learning, building, failing, and rebuilding.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              I started my journey as a Roblox developer and 3D artist, working with real teams, shipping real products, and learning how software behaves in the real world — not just in tutorials. Over time, that evolved into a deeper obsession with AI, automation, and productivity tools.
            </p>

            {/* Why Twe Exists */}
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-6 mb-8">
              <div className="flex items-start space-x-3 mb-4">
                <Zap className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <h2 className="text-2xl font-bold text-white">Why Twe Exists</h2>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Twe was created to solve a personal problem:
              </p>
              <p className="text-xl text-orange-400 font-semibold italic mb-4">
                "Why does interacting with a computer still feel slow in an AI era?"
              </p>
              <p className="text-gray-300 leading-relaxed">
                Typing, switching windows, repeating commands — all friction.
                <br />
                Twe is my attempt to build a true desktop AI companion, not just another chatbot.
              </p>
            </div>

            {/* What Drives Me */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/40 rounded-lg p-6 border border-white/5">
                <Target className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Discipline over motivation</h3>
              </div>
              <div className="bg-black/40 rounded-lg p-6 border border-white/5">
                <TrendingUp className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Shipping over perfection</h3>
              </div>
              <div className="bg-black/40 rounded-lg p-6 border border-white/5">
                <Award className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Long-term vision over short-term hype</h3>
              </div>
            </div>

            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              I believe great tools are built by people who actually use them daily — and Twe is one of those tools.
            </p>

            {/* Future */}
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">This Is Just the Beginning</h2>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Twe Voice Assistant is actively evolving.
                <br />
                New features, smarter automation, and deeper system control are already in development.
              </p>
              <p className="text-xl text-orange-400 font-semibold">
                If you're using Twe, you're not just a user — you're part of the journey.
              </p>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-2xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-white mb-6 text-center">
            About
          </h2>
          
          <div className="text-center mb-8">
            <p className="text-2xl text-orange-500 font-bold italic mb-6">
              "God, show me your challenges. I'm ready" - Tusdang
            </p>
          </div>

          <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
            <p>
              <strong className="text-white">Tu Dang (18 y/o)</strong> received his first interview invitation for a game company at the age of 15 and has been involved in numerous game development projects on Roblox, which are run by big studios, at just 17.
            </p>

            <p>
              At the age when other peers in his country (Vietnam) are trying to get good marks at school, he immersed himself in the world of creativity and business rules, thriving and trying deep into start-up game projects without being afraid of failures, which is never taught at school.
            </p>

            <p>
              Although having the burden and restrictions from conventional parents, who forced him to study and crashed his only laptop, he didn't stop chasing his dreams and tried to prove everyone wrong up to now.
            </p>

            <p>
              At 18, he founded <strong className="text-orange-500">TweNa</strong>, became a part of Superbullet Studio partnered with DoBig. His TweNa Inc starts to run games and sell merchandise (shirts), and is going to develop mobile apps in the future.
            </p>

            <p className="text-xl text-white font-semibold mt-8">
              And yeah, I'm Tu Dang, people mainly call me Tusdang on the internet (Discord). I will try my best, no matter how hard the journey, how challenging it would be, I will build a billion-dollar company in the upcoming years.
            </p>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">
            Want to connect or collaborate?
          </p>
          <a
            href="mailto:contact@twe-assistant.com"
            className="text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200 text-lg"
          >
            contact@twe-assistant.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default About;